<template>
  <div class="operation-logs-container">
    <div class="page-header"><h2><span class="breadcrumb-level">系统管理</span><span class="breadcrumb-sep">/</span><span class="breadcrumb-level">日志管理</span><span class="breadcrumb-sep">/</span><span class="breadcrumb-level breadcrumb-current">操作日志</span></h2></div>
    <el-alert
        title="日志保存说明"
        type="info"
        show-icon
        :closable="false"
        style="margin-bottom: 20px"
      >
        系统自动保存最近六个月的操作日志，超过六个月的日志将被定期清理
      </el-alert>
    <el-card shadow="hover">
      <div class="card-header">
        <el-input
          v-model="searchQuery"
          placeholder="搜索操作路径或参数"
          style="width: 300px; margin-right: 10px;"
          clearable
        >
          <template #prefix>
            <el-icon><search /></el-icon>
          </template>
        </el-input>
        <el-select
          v-model="filters.method"
          placeholder="按请求方法筛选"
          style="width: 150px; margin-right: 10px;"
          clearable
        >
          <el-option label="GET" value="GET" />
          <el-option label="POST" value="POST" />
          <el-option label="PUT" value="PUT" />
          <el-option label="DELETE" value="DELETE" />
          <el-option label="PATCH" value="PATCH" />
        </el-select>
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          style="width: 300px; margin-right: 10px;"
        />
        <el-button type="primary" @click="handleSearch">
          <el-icon><search /></el-icon> 搜索
        </el-button>
        <el-button @click="handleClearFilters">
          <el-icon><refresh-left /></el-icon> 清除筛选
        </el-button>
      </div>
      
      <el-table :data="operationLogs" border stripe style="width: 100%; margin-top: 20px;" height="600" v-loading="loading">
        <el-table-column label="请求信息" min-width="300">
          <template #default="scope">
            <div>
              <div style="display: flex; align-items: center; gap: 10px;">
                <el-tag :type="getMethodTag(scope.row.method)" size="small">
                  {{ scope.row.method }}
                </el-tag>
                <el-tag :type="getStatusCodeTag(scope.row.status)" size="small">
                  {{ scope.row.status }}
                </el-tag>
              </div>
              <div style="margin-top: 5px; font-size: 14px;">
                {{ scope.row.path || '-' }}
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="操作人/IP" width="200">
          <template #default="scope">
            <div>
              <div>{{ scope.row.user_name || '-' }}</div>
              <div style="font-size: 12px; color: #999;">{{ scope.row.ip || '-' }}</div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="input" label="请求参数" min-width="200">
          <template #default="scope">
            <el-button type="text" @click="handleViewParams(scope.row)">
              <el-icon><view /></el-icon> 查看
            </el-button>
          </template>
        </el-table-column>
        <el-table-column prop="output" label="返回参数" min-width="200">
          <template #default="scope">
            <el-button type="text" @click="handleViewOutput(scope.row)">
              <el-icon><view /></el-icon> 查看
            </el-button>
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="操作时间" width="200">
          <template #default="scope">
            {{ formatDateTime(scope.row.created_at) }}
          </template>
        </el-table-column>
      </el-table>
      
      <div class="card-footer" style="margin-top: 20px;">
        <el-pagination
          :current-page="currentPage"
          :page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>
    
    <!-- 请求参数查看对话框 -->
    <el-dialog
      v-model="paramsDialogVisible"
      title="请求参数"
      width="800px"
    >
      <pre style="background-color: #f5f7fa; padding: 15px; border-radius: 4px; overflow: auto; max-height: 500px;">
        {{ JSON.stringify(selectedLog.input || {}, null, 2) }}
      </pre>
    </el-dialog>
    
    <!-- 返回参数查看对话框 -->
    <el-dialog
      v-model="outputDialogVisible"
      title="返回参数"
      width="800px"
    >
      <pre style="background-color: #f5f7fa; padding: 15px; border-radius: 4px; overflow: auto; max-height: 500px;">
        {{ JSON.stringify(selectedLog.output || {}, null, 2) }}
      </pre>
    </el-dialog>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { ElMessage } from 'element-plus';
import { Search, RefreshLeft, View } from '@element-plus/icons-vue';
import operationLogApi from '../../api/operationLog';

export default {
  name: 'OperationLogs',
  components: {
    Search,
    RefreshLeft,
    View
  },
  setup() {
    const searchQuery = ref('');
    const filters = ref({
      method: ''
    });
    const dateRange = ref([]);
    const operationLogs = ref([]);
    const currentPage = ref(1);
    const pageSize = ref(20);
    const total = ref(0);
    const paramsDialogVisible = ref(false);
    const outputDialogVisible = ref(false);
    const selectedLog = ref({});
    const loading = ref(false);
    
    // 获取系统管理/操作日志列表
    const fetchOperationLogs = async () => {
      loading.value = true;
      try {
        const params = {
          page: currentPage.value,
          page_size: pageSize.value,
          search: searchQuery.value,
          method: filters.value.method,
          start_date: dateRange.value[0] ? dateRange.value[0].format('YYYY-MM-DD HH:mm:ss') : '',
          end_date: dateRange.value[1] ? dateRange.value[1].format('YYYY-MM-DD HH:mm:ss') : ''
        };
        console.log('请求参数:', params);
        const response = await operationLogApi.getOperationLogs(params);
        console.log('API响应:', response);
        
        // 处理不同的响应格式
        let logsData = [];
        let logsTotal = 0;
        
        if (response) {
          // 标准格式：response.data 和 response.total
          if (response.data && Array.isArray(response.data)) {
            logsData = response.data;
            logsTotal = response.pagination?.total || response.total || 0;
          } 
          // 响应本身就是数组的格式
          else if (Array.isArray(response)) {
            logsData = response;
            logsTotal = response.length;
          }
        }
        
        console.log('处理后的数据:', logsData);
        console.log('处理后的总数:', logsTotal);
        
        operationLogs.value = logsData;
        total.value = logsTotal;
        
        if (logsData.length === 0) {
          ElMessage.info('暂无系统管理/操作日志');
        }
      } catch (error) {
        console.error('获取系统管理/操作日志失败:', error);
        ElMessage.error('获取系统管理/操作日志失败：' + (error.message || '未知错误'));
        operationLogs.value = [];
        total.value = 0;
      } finally {
        loading.value = false;
      }
    };
    
    // 页面加载时获取系统管理/操作日志
    onMounted(() => {
      fetchOperationLogs();
    });
    
    // 搜索系统管理/操作日志
    const handleSearch = () => {
      currentPage.value = 1;
      fetchOperationLogs();
    };
    
    // 清除筛选条件
    const handleClearFilters = () => {
      searchQuery.value = '';
      filters.value.method = '';
      dateRange.value = [];
      currentPage.value = 1;
      fetchOperationLogs();
    };
    
    // 查看请求参数
    const handleViewParams = (log) => {
      selectedLog.value = log;
      paramsDialogVisible.value = true;
    };
    
    // 查看返回参数
    const handleViewOutput = (log) => {
      selectedLog.value = log;
      outputDialogVisible.value = true;
    };
    
    // 处理分页大小变化
    const handleSizeChange = (size) => {
      pageSize.value = size;
      fetchOperationLogs();
    };
    
    // 处理当前页变化
    const handleCurrentChange = (current) => {
      currentPage.value = current;
      fetchOperationLogs();
    };
    
    // 获取请求方法标签颜色
    const getMethodTag = (method) => {
      const tagMap = {
        GET: 'success',
        POST: 'primary',
        PUT: 'warning',
        DELETE: 'danger',
        PATCH: 'info'
      };
      return tagMap[method] || 'info';
    };
    
    // 获取状态码标签颜色
    const getStatusCodeTag = (code) => {
      if (code >= 200 && code < 300) {
        return 'success';
      } else if (code >= 300 && code < 400) {
        return 'warning';
      } else {
        return 'danger';
      }
    };
    
    // 格式化日期时间
    const formatDateTime = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    };
    
    return {
      searchQuery,
      filters,
      dateRange,
      operationLogs,
      currentPage,
      pageSize,
      total,
      paramsDialogVisible,
      outputDialogVisible,
      selectedLog,
      loading,
      handleSearch,
      handleClearFilters,
      handleViewParams,
      handleViewOutput,
      handleSizeChange,
      handleCurrentChange,
      getMethodTag,
      getStatusCodeTag,
      formatDateTime
    };
  }
};
</script>

<style scoped>


.operation-logs-container h2 {
  margin-bottom: 20px;
  color: #333;
}

.card-header {
  display: flex;
  align-items: center;
  margin-bottom: 0;
  flex-wrap: wrap;
  gap: 10px;
}

.card-footer {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}
</style>